import { createContext, ReactNode, useContext } from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import * as PopoverPrimitive from '@radix-ui/react-popover'

import styles from './Tooltip.module.css'

type Side = 'top' | 'bottom' | 'left' | 'right'
type Align = 'start' | 'center' | 'end'

type Placement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end'

export type TooltipProps = {
  arrowOffset?: number
  children: ReactNode
  container?: HTMLElement | null
  contentPadding?: number
  tooltipContent: ReactNode
  placement?: Placement
  trigger?: 'click' | 'mouseenter'
  interactive?: boolean
  zIndex?: number
  maxWidth?: number
}

const parsePlacement = (placement: Placement = 'top'): { side: Side; align: Align } => {
  const [side, align] = placement.split('-') as [Side, Align | undefined]
  return { side, align: align || 'center' }
}

// Context to provide a container for tooltips rendered inside modals/dialogs
const TooltipContainerContext = createContext<HTMLElement | null>(null)

export const TooltipContainerProvider = ({
  children,
  container,
}: {
  children: ReactNode
  container: HTMLElement | null
}) => {
  return (
    <TooltipContainerContext.Provider value={container}>
      {children}
    </TooltipContainerContext.Provider>
  )
}

// Hook to get the nearest dialog/modal element for portaling
const useTooltipContainer = (explicitContainer?: HTMLElement | null) => {
  const contextContainer = useContext(TooltipContainerContext)

  // For tooltips inside modals, use the context container which is set by ModalBase
  // For regular tooltips, default to null (renders to body)
  return explicitContainer ?? contextContainer ?? null
}

export const Tooltip = ({
  children,
  container: explicitContainer,
  tooltipContent,
  placement = 'top',
  trigger = 'mouseenter',
  maxWidth,
}: TooltipProps) => {
  const { side, align } = parsePlacement(placement)
  const container = useTooltipContainer(explicitContainer)
  const contentStyle = maxWidth ? { maxWidth: `${maxWidth}px` } : undefined

  if (trigger === 'click') {
    return (
      <PopoverPrimitive.Root>
        <PopoverPrimitive.Trigger asChild>{children}</PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal container={container}>
          <PopoverPrimitive.Content
            className={styles.content}
            style={contentStyle}
            side={side}
            align={align}
            sideOffset={5}
          >
            {tooltipContent}
            <PopoverPrimitive.Arrow className={styles.arrow} />
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    )
  }

  return (
    <TooltipPrimitive.Provider delayDuration={0}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild onFocus={(e) => e.preventDefault()}>
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal container={container}>
          <TooltipPrimitive.Content
            className={styles.content}
            style={contentStyle}
            side={side}
            align={align}
            sideOffset={5}
          >
            {tooltipContent}
            <TooltipPrimitive.Arrow className={styles.arrow} />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}
